import { useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, ScrollView, RefreshControl, Share, StyleSheet } from 'react-native'
import ViewShot from 'react-native-view-shot'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useBadges } from '../../lib/useBadges'
import { useShowcase, saveShowcase } from '../../lib/useShowcase'
import { shareViewAsImage } from '../../lib/shareImage'
import { BADGE_GROUPS, countEarned, countTotal } from '../../lib/badges'
import { Card, SectionLabel } from '../../components/ui'
import SegmentedTabs from '../../components/SegmentedTabs'
import { ListSkeleton, ErrorState } from '../../components/StateViews'
import MemberPicker from '../../components/Leagues/MemberPicker'
import BadgeCard from '../../components/Badges/BadgeCard'
import BadgeSheet from '../../components/Badges/BadgeSheet'
import GtJerseysCard from '../../components/Badges/GtJerseysCard'
import SpecialJerseysCard from '../../components/Badges/SpecialJerseysCard'
import AlboDoro from '../../components/Leagues/AlboDoro'
import ShowcaseRow from '../../components/Badges/ShowcaseRow'
import ShowcaseEditSheet from '../../components/Badges/ShowcaseEditSheet'
import BadgeShareCard, { BADGE_SHARE_W } from '../../components/Badges/BadgeShareCard'
import { useBadgeText } from '../../components/Badges/BadgeCard'

// Album dei badge di una lega: tutte le famiglie per gruppo, con avanzamento e rarita'. Si puo' guardare quello degli altri membri.
export default function BadgesAlbum() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const insets = useSafeAreaInsets()
  const { user, leagueId, leagueName } = useAuth()
  const txt = useBadgeText()
  const [members, setMembers] = useState([])
  const [selected, setSelected] = useState(user?.id)
  const [filter, setFilter] = useState('all')
  const [open, setOpen] = useState(null)       // categoria aperta nel dettaglio
  const [refreshing, setRefreshing] = useState(false)
  const { loading, error, enabled, families, days, reload } = useBadges(leagueId, selected)
  const showcase = useShowcase(leagueId, selected)
  const [editShowcase, setEditShowcase] = useState(false)
  const [shareTarget, setShareTarget] = useState(null)      // { family, level } da trasformare in immagine
  const shotRef = useRef(null)

  useEffect(() => {
    if (!leagueId) return
    supabase.rpc('fn_league_members', { p_id_league: leagueId }).then(({ data }) => {
      setMembers((data ?? []).map(m => ({ out_id_user: m.id_user, out_display_name: m.display_name ?? '—' })))
    })
  }, [leagueId])

  const earned = countEarned(families)
  const total = countTotal(families)
  const pct = total > 0 ? Math.round((earned / total) * 100) : 0
  const isMe = selected === user?.id
  const name = members.find(m => m.out_id_user === selected)?.out_display_name ?? ''

  const groups = useMemo(() => BADGE_GROUPS.map(g => {
    const all = families.filter(f => f.group === g)
    const list = all.filter(f => filter === 'all' ? true : filter === 'earned' ? f.earned : !f.complete)
    return {
      key: g, list,
      got: all.reduce((n, f) => n + f.levels.filter(l => l.earned).length, 0),
      tot: all.reduce((n, f) => n + f.levels.length, 0),
    }
  }).filter(g => g.list.length > 0), [families, filter])

  const onRefresh = async () => { setRefreshing(true); reload(); setTimeout(() => setRefreshing(false), 600) }
  const openFamily = families.find(f => f.category === open) ?? null

  // Immagine del badge: si disegna la scheda fissa fuori schermo, poi si cattura e si condivide (ripiego: testo)
  const handleShare = async (family, level) => {
    setShareTarget({ family, level })
    await new Promise(r => setTimeout(r, 200))
    const name = txt.levelTitle(family, level)
    try {
      await shareViewAsImage(shotRef, { fileName: `virtua-badge-${family.category.toLowerCase()}-${level.level}`, dialogTitle: t('badges.share_dialog', { name }) })
    } catch {
      try { await Share.share({ message: t('badges.share_text', { name, site: 'virtuacycling.com' }) }) } catch {/* ignore */}
    }
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: tc.bg }}
      contentContainerStyle={{ padding: 16, paddingBottom: 40 + insets.bottom, gap: 16 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={tc.rosa} />}
    >
      {members.length > 1 && (
        <MemberPicker members={members} value={selected} onChange={setSelected} placeholder="—" title={t('badges.player')} />
      )}

      {loading && families.length === 0 ? (
        <ListSkeleton rows={6} />
      ) : error && families.length === 0 ? (
        <ErrorState onRetry={reload} message={t('badges.load_error')} />
      ) : (
        <>
          {/* In alto: quanti badge, e i tre in evidenza (quelli che si vedono in Home) */}
          {enabled && (
            <Card style={{ marginBottom: 0 }}>
              <View style={s.sumRow} accessible accessibilityLabel={t('badges.earned_of', { earned, total })}>
                <Text style={[s.sumBig, { color: tc.text }]}>{earned}<Text style={[s.sumTot, { color: tc.textMuted }]}> / {total}</Text></Text>
                <Text style={[s.sumPct, { color: tc.rosaText }]}>{pct}%</Text>
              </View>
              <View style={[s.barBg, { backgroundColor: tc.border }]}>
                <View style={[s.barFill, { backgroundColor: tc.rosa, width: `${pct}%` }]} />
              </View>
              {earned === 0 ? (
                <Text style={[s.note, { color: tc.textMuted, marginTop: 8, textAlign: 'left' }]}>
                  {isMe ? t('badges.none_me') : t('badges.none_other', { name })}
                </Text>
              ) : (
                <View style={{ marginTop: 14 }}>
                  <View style={s.showHead}>
                    <Text style={[s.label, { color: tc.textMuted }]}>{t('badges.showcase_title')}</Text>
                    {isMe && <Text onPress={() => setEditShowcase(true)} accessibilityRole="button" style={[s.edit, { color: tc.rosaText }]}>{t('badges.showcase_edit')}</Text>}
                  </View>
                  {showcase.items.length > 0
                    ? <ShowcaseRow items={showcase.items} size={52} onPress={isMe ? () => setEditShowcase(true) : () => {}} />
                    : isMe && <Text style={[s.note, { color: tc.textMuted, textAlign: 'left', paddingVertical: 0 }]}>{t('badges.showcase_none')}</Text>}
                </View>
              )}
            </Card>
          )}

          {/* Maglie: Grandi Giri e maglie speciali */}
          <Text style={[s.section, { color: tc.text }]} accessibilityRole="header">{t('badges.section_jerseys')}</Text>
          {enabled && <GtJerseysCard days={days} />}
          <SpecialJerseysCard leagueId={leagueId} userId={selected} leagueName={leagueName} />

          {/* Albo d'oro: podi di gara */}
          <Text style={[s.section, { color: tc.text }]} accessibilityRole="header">{t('leagues.albo')}</Text>
          {members.length > 0 && <AlboDoro stagionale={members} leagueId={leagueId} currentUserId={user?.id} userId={selected} />}

          {/* Badge */}
          <Text style={[s.section, { color: tc.text }]} accessibilityRole="header">{t('badges.title')}</Text>
          {!enabled ? (
            <Text style={[s.note, { color: tc.textMuted }]}>{t('badges.not_enabled')}</Text>
          ) : (
            <>
              <SegmentedTabs
                options={[{ key: 'all', label: t('badges.filter_all') }, { key: 'earned', label: t('badges.filter_earned') }, { key: 'todo', label: t('badges.filter_todo') }]}
                value={filter}
                onChange={setFilter}
              />
              {groups.length === 0 && <Text style={[s.note, { color: tc.textMuted }]}>{t('badges.empty_filter')}</Text>}
              {groups.map(g => (
                <View key={g.key} style={{ gap: 10 }}>
                  <View style={s.groupHead}>
                    <Text style={[s.groupTitle, { color: tc.text }]} accessibilityRole="header">{t(`badges.group_${g.key}`)}</Text>
                    <Text style={[s.groupCount, { color: tc.textMuted }]}>{g.got} / {g.tot}</Text>
                  </View>
                  <View style={s.grid}>
                    {g.list.map(f => <BadgeCard key={f.category} family={f} onPress={() => setOpen(f.category)} />)}
                  </View>
                </View>
              ))}
            </>
          )}
        </>
      )}

      <BadgeSheet family={openFamily} onClose={() => setOpen(null)} onShare={isMe ? handleShare : undefined} />
      {editShowcase && (
        <ShowcaseEditSheet
          families={families}
          initial={showcase.custom ? showcase.items.map(b => b.code) : []}
          onClose={() => setEditShowcase(false)}
          onSave={async (codes) => (await saveShowcase(leagueId, codes)).ok}
        />
      )}

      {/* immagine di condivisione, fuori schermo */}
      <ViewShot ref={shotRef} options={{ format: 'png', quality: 1, pixelRatio: 3 }} style={{ position: 'absolute', top: -9999, left: 0, width: BADGE_SHARE_W }}>
        <BadgeShareCard family={shareTarget?.family} level={shareTarget?.level} leagueName={leagueName} />
      </ViewShot>
    </ScrollView>
  )
}

const s = StyleSheet.create({
  sumRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 },
  sumBig: { fontFamily: fonts.condensedBold, fontSize: 34 },
  sumTot: { fontFamily: fonts.condensedBold, fontSize: 20 },
  sumPct: { fontFamily: fonts.condensedBold, fontSize: 20 },
  barBg: { height: 6, borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
  note: { fontSize: 14, lineHeight: 20, textAlign: 'center', paddingVertical: 12 },
  section: { fontFamily: fonts.condensedBold, fontSize: 22, marginTop: 8 },
  label: { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: fonts.condensedBold },
  showHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, minHeight: 44 },
  edit: { fontFamily: fonts.condensedBold, fontSize: 14, minHeight: 44, textAlignVertical: 'center', lineHeight: 44, paddingHorizontal: 4 },
  groupHead: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
  groupTitle: { fontFamily: fonts.condensedBold, fontSize: 18 },
  groupCount: { fontSize: 13 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between' },
})
